import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { RoleService } from 'app/core/roles/role.service';
import { RoleEnum } from 'app/core/roles/role.types';
import {
    compactNavigation,
    defaultNavigation,
    futuristicNavigation,
    horizontalNavigation,
} from 'app/mock-api/common/navigation/data';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private readonly _compactNavigation: FuseNavigationItem[] =
        compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[] =
        defaultNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] =
        futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] =
        horizontalNavigation;

    constructor(
        private _fuseMockApiService: FuseMockApiService,
        private _roleService: RoleService,
    ) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
            // Fill compact navigation children using the default navigation
            this._compactNavigation.forEach((compactNavItem) => {
                this._defaultNavigation.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === compactNavItem.id) {
                        compactNavItem.children = cloneDeep(
                            defaultNavItem.children
                        );
                    }
                });
            });

            // Fill futuristic navigation children using the default navigation
            this._futuristicNavigation.forEach((futuristicNavItem) => {
                this._defaultNavigation.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === futuristicNavItem.id) {
                        futuristicNavItem.children = cloneDeep(
                            defaultNavItem.children
                        );
                    }
                });
            });

            // Fill horizontal navigation children using the default navigation
            this._horizontalNavigation.forEach((horizontalNavItem) => {
                this._defaultNavigation.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === horizontalNavItem.id) {
                        horizontalNavItem.children = cloneDeep(
                            defaultNavItem.children
                        );
                    }
                });
            });

            const reportsHidden = () => !this._roleService.isInRole([RoleEnum.Role.Administrator, RoleEnum.Role.Analyst]);
            const usersHidden = () => !this._roleService.isInRole([RoleEnum.Role.Administrator, RoleEnum.Role.UserManager]);

            const applyHidden = (nav: FuseNavigationItem[]) => {
                const reportsItem = nav.find(i => i.id === 'reports');
                if (reportsItem) reportsItem.hidden = reportsHidden;

                const usersItem = nav.find(i => i.id === 'users');
                if (usersItem) usersItem.hidden = usersHidden;
            };

            const defaultNav = cloneDeep(this._defaultNavigation);
            const compactNav = cloneDeep(this._compactNavigation);
            const futuristicNav = cloneDeep(this._futuristicNavigation);
            const horizontalNav = cloneDeep(this._horizontalNavigation);

            applyHidden(defaultNav);
            applyHidden(compactNav);
            applyHidden(futuristicNav);
            applyHidden(horizontalNav);

            // Return the response
            return [
                200,
                {
                    compact: compactNav,
                    default: defaultNav,
                    futuristic: futuristicNav,
                    horizontal: horizontalNav,
                },
            ];
        });
    }
}
